/**
 * 価格表 (Excel/CSV) 取込の共通パーツ。
 *
 * 「Excel / CSV から取込」(新規カタログ作成) と
 * 「CSV 一括上書き」(選択中カタログへ上書き) の両ダイアログで共有する。
 * ★ パースは必ず catalogSheetParser.parseCatalogSheet を通すこと。
 *   ダイアログごとに列インデックスを書くと、次のフォーマット変更でまた片方だけ壊れる。
 */
import { AlertTriangle } from 'lucide-react';
import * as XLSX from 'xlsx';

import {
  COLUMN_FIELD_LABELS,
  columnLetter,
  parseCatalogSheet,
  type ParsedCatalogRow,
  type ParsedSheet,
  type ResolvedColumns,
} from './catalogSheetParser';

import { Badge } from '@/components/ui/badge';


/** Callable に投げる 1 件分 (bulkImportToCatalog の items 要素)。 */
export interface CatalogImportItem {
  name: string;
  modelName: string;
  variation: string | null;
  sku: string;
  /**
   * シートに「製品カテゴリ」列が無い場合はこのキー自体を省く (undefined)。
   * サーバ側 (bulkImport) はキーが無い item の productCategory を書き換えないので、
   * 旧フォーマットの価格表で上書きしても既存の製品カテゴリが保持される。
   */
  productCategory?: string | null;
  description: string | null;
  costPrice: number;
  priceExcludingTax: number;
  priceIncludingTax: number;
  supplierName: string | null;
}

/** bulkImportToCatalog の 1 リクエスト上限 (functions 側 bulkImport.ts と揃えること)。 */
export const BULK_IMPORT_MAX_ROWS = 1000;

/** ファイル (xlsx / xls / csv) を読んでパースする。 */
export async function readCatalogSheetFile(
  file: File,
  options?: { defaultSupplierName?: string | null },
): Promise<ParsedSheet> {
  const ext = (file.name.split('.').pop() ?? '').toLowerCase();
  let wb: XLSX.WorkBook;
  if (ext === 'csv') {
    const text = await file.text();
    wb = XLSX.read(text, { type: 'string', raw: false });
  } else {
    const buf = await file.arrayBuffer();
    wb = XLSX.read(buf, { type: 'array' });
  }
  const sheet = wb.Sheets[wb.SheetNames[0]];
  if (!sheet) throw new Error('シートが空です');
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: null,
  }) as unknown[][];
  return parseCatalogSheet(rows, options);
}

/**
 * パース結果 → Callable payload。validationError のある行は除外する。
 *
 * @param includeProductCategory
 *   シートに「製品カテゴリ」列があったか (= parsed.columns.productCategory != null)。
 *   false のときは productCategory キー自体を落とし、サーバ側で既存値を温存させる。
 *   ★ ここで false を渡さないと、旧フォーマットの価格表で一括上書きした瞬間に
 *     既存の製品カテゴリが全件 null になる。
 */
export function toImportItems(
  rows: ParsedCatalogRow[],
  includeProductCategory = true,
): CatalogImportItem[] {
  const seen = new Set<string>();
  const out: CatalogImportItem[] = [];
  for (const r of rows) {
    if (r.validationError) continue;
    if (seen.has(r.sku)) continue; // SKU 重複は先勝ち (サーバ側も同じ挙動)
    seen.add(r.sku);
    out.push({
      name: r.name,
      modelName: r.modelName,
      variation: r.variation,
      sku: r.sku,
      ...(includeProductCategory
        ? { productCategory: r.productCategory }
        : {}),
      description: r.description,
      costPrice: r.costPrice,
      priceExcludingTax: r.priceExcludingTax,
      priceIncludingTax: r.priceIncludingTax,
      supplierName: r.supplierName,
    });
  }
  return out;
}

export interface SheetSummary {
  total: number;
  valid: number;
  invalid: number;
  withCategory: number;
}

export function summarize(rows: ParsedCatalogRow[]): SheetSummary {
  const valid = rows.filter((r) => !r.validationError).length;
  return {
    total: rows.length,
    valid,
    invalid: rows.length - valid,
    withCategory: rows.filter((r) => !r.validationError && r.productCategory)
      .length,
  };
}

// ─────────────────────────────────────────────
// 列マッピング表示 — 「どの列をどう読んだか」を必ず人間に見せる。
// 静かに 1 列ずれて取込まれる事故を、実行前に気付けるようにするため。
// ─────────────────────────────────────────────

const MAPPING_ORDER: Array<keyof ResolvedColumns> = [
  'name',
  'productCategory',
  'sku',
  'modelName',
  'costPrice',
  'priceExcludingTax',
  'priceIncludingTax',
  'description',
  'supplierName',
];

export function CatalogColumnMap({
  parsed,
}: {
  parsed: Pick<ParsedSheet, 'columns' | 'headerRowIndex' | 'headerLabels' | 'warnings'>;
}) {
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
          列マッピング
        </h4>
        <span className="text-[10px] text-[var(--text-muted)]">
          {parsed.headerRowIndex >= 0
            ? `ヘッダー行: ${parsed.headerRowIndex + 1} 行目`
            : 'ヘッダー未検出 (旧フォーマットの固定列で読取り)'}
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {MAPPING_ORDER.map((key) => {
          const idx = parsed.columns[key];
          const resolved = idx != null;
          const sheetLabel =
            resolved && parsed.headerLabels[idx] ? parsed.headerLabels[idx] : null;
          return (
            <span
              key={key}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${
                resolved
                  ? 'border-[var(--border)] bg-[var(--bg-input)] text-[var(--text-secondary)]'
                  : 'border-dashed border-[var(--border)] text-[var(--text-muted)]'
              }`}
              title={sheetLabel ? `シート上の見出し: ${sheetLabel}` : undefined}
            >
              <span className="font-medium">{COLUMN_FIELD_LABELS[key]}</span>
              <span className="text-[var(--text-muted)]">
                {resolved ? `→ ${columnLetter(idx)} 列` : '→ なし'}
              </span>
            </span>
          );
        })}
      </div>

      {parsed.warnings.length > 0 && (
        <ul className="space-y-1 pt-1">
          {parsed.warnings.map((w, i) => (
            <li
              key={i}
              className="flex items-start gap-1.5 text-[11px] text-[var(--accent-amber)]"
            >
              <AlertTriangle className="size-3 shrink-0 mt-0.5" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// 取込プレビュー表
// ─────────────────────────────────────────────

export function CatalogRowsPreview({
  rows,
  maxHeightClass = 'max-h-[320px]',
}: {
  rows: ParsedCatalogRow[];
  maxHeightClass?: string;
}) {
  return (
    <div
      className={`rounded-md border border-[var(--border)] overflow-hidden ${maxHeightClass} overflow-y-auto`}
    >
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-[var(--bg-elevated)] border-b border-[var(--border)]">
          <tr className="text-left text-[var(--text-muted)] uppercase tracking-wider">
            <th className="px-3 py-2 font-medium">製品名</th>
            <th className="px-3 py-2 font-medium">製品カテゴリ</th>
            <th className="px-3 py-2 font-medium">SKU</th>
            <th className="px-3 py-2 font-medium text-right">仕入価格</th>
            <th className="px-3 py-2 font-medium text-right">税抜</th>
            <th className="px-3 py-2 font-medium text-right">税込</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((it, i) => (
            <tr
              key={`${it.sku}-${i}`}
              className={`border-b border-[var(--border)] last:border-b-0 ${
                it.validationError ? 'bg-[var(--accent-red-subtle)]' : ''
              }`}
            >
              <td className="px-3 py-1.5">
                <div className="text-[var(--text-primary)]">{it.name}</div>
                {it.description && (
                  <div className="text-[10px] text-[var(--text-muted)] mt-0.5 line-clamp-1">
                    {it.description}
                  </div>
                )}
                {it.validationError && (
                  <div className="text-[10px] text-[var(--accent-red)] mt-0.5">
                    ⚠ {it.validationError}
                  </div>
                )}
              </td>
              <td className="px-3 py-1.5">
                {it.productCategory ? (
                  <Badge variant="default">{it.productCategory}</Badge>
                ) : (
                  <span className="text-[10px] text-[var(--text-muted)]">—</span>
                )}
              </td>
              <td className="px-3 py-1.5 font-mono text-[10px] text-[var(--text-muted)]">
                {it.sku}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums text-[var(--text-muted)]">
                ¥{it.costPrice.toLocaleString('ja-JP')}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                ¥{it.priceExcludingTax.toLocaleString('ja-JP')}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                ¥{it.priceIncludingTax.toLocaleString('ja-JP')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 検出/有効/エラー件数のサマリ行。 */
export function CatalogSheetSummaryBar({
  summary,
  onReset,
}: {
  summary: SheetSummary;
  onReset?: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] text-xs">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>
          📋 検出: <strong>{summary.total} 件</strong>
        </span>
        <span>
          / 有効:{' '}
          <strong className="text-[var(--accent-green)]">{summary.valid}</strong>
        </span>
        <span>
          / エラー:{' '}
          <strong
            className={
              summary.invalid > 0
                ? 'text-[var(--accent-red)]'
                : 'text-[var(--text-muted)]'
            }
          >
            {summary.invalid}
          </strong>
        </span>
        <span className="text-[var(--text-muted)]">
          / 製品カテゴリあり: <strong>{summary.withCategory}</strong>
        </span>
      </div>
      {onReset && (
        <button
          type="button"
          className="text-[var(--accent)] hover:underline whitespace-nowrap"
          onClick={onReset}
        >
          別のファイルを選び直す
        </button>
      )}
    </div>
  );
}
